import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fiscalFxRateInputSchema, type FiscalFxRateInput, type FiscalFxRate } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { InlineHelp } from '@/features/help';
import { useFiscalRates, useReviewFiscalRate } from '../api/use-fiscal-fx';
import { FiscalSourceReviews } from '../components/fiscal-source-reviews';

const normalizeQuote = (value: string) =>
  value
    .trim()
    .replace(',', '.')
    .replace(/^\.(\d+)$/u, '0.$1')
    .replace(/^(\d+)\.$/u, '$1');
const resolveRate = zodResolver(fiscalFxRateInputSchema);
const defaults = (): FiscalFxRateInput => ({
  sourceCurrency: '',
  method: 'MONTHLY_BMF',
  quoteDirection: 'FOREIGN_PER_EUR',
  quote: '',
  validFrom: '',
  validTo: '',
  sourceUrl: '',
  sourceEvidence: '',
  sourcePublishedOn: '',
  approvalReference: null,
  approvalEvidence: null,
  approvalValidFrom: null,
  approvalValidTo: null,
  supersedesId: null,
  idempotencyKey: crypto.randomUUID(),
});
const mainFields = [
  'sourceCurrency',
  'quote',
  'validFrom',
  'validTo',
  'sourceUrl',
  'sourcePublishedOn',
  'sourceEvidence',
] as const;
const approvalFields = [
  'approvalReference',
  'approvalValidFrom',
  'approvalValidTo',
  'approvalEvidence',
] as const;
export function FiscalFxRoute() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const query = useFiscalRates();
  const mutation = useReviewFiscalRate();
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<FiscalFxRateInput>({
    resolver: (values, context, options) =>
      resolveRate({ ...values, quote: normalizeQuote(values.quote) }, context, options),
    defaultValues: defaults(),
    shouldFocusError: true,
  });
  const method = form.watch('method');
  const correction = form.watch('supersedesId');
  const submit = form.handleSubmit(async (input) => {
    try {
      await mutation.mutateAsync(input);
      form.reset(defaults());
      toast.success(t('fiscalFx.saved'));
    } catch {
      form.setError('root.server', { message: 'fiscalFx.error' });
    }
  });
  const correct = (rate: FiscalFxRate) => {
    form.reset({ ...rate, supersedesId: rate.id, idempotencyKey: crypto.randomUUID() });
    mutation.reset();
    form.setFocus('quote');
  };
  const field = (name: (typeof mainFields)[number] | (typeof approvalFields)[number]) => {
    const error = form.formState.errors[name];
    const textArea = name === 'sourceEvidence' || name === 'approvalEvidence';
    const registration = form.register(name, {
      setValueAs: (value: string) => (name.startsWith('approval') && !value ? null : value),
    });
    return (
      <div className={textArea ? 'space-y-1 sm:col-span-2' : 'space-y-1'} key={name}>
        <Label htmlFor={`fiscal-${name}`}>{t(`fiscalFx.${name}`)}</Label>
        {textArea ? (
          <Textarea id={`fiscal-${name}`} {...registration} aria-invalid={!!error} rows={4} />
        ) : (
          <Input
            id={`fiscal-${name}`}
            {...registration}
            aria-invalid={!!error}
            type={
              name.endsWith('From') || name.endsWith('To') || name.endsWith('On')
                ? 'date'
                : name === 'sourceUrl'
                  ? 'url'
                  : 'text'
            }
            inputMode={name === 'quote' ? 'decimal' : undefined}
          />
        )}
        {error ? (
          <p className="text-sm text-destructive">{t(error.message ?? 'fiscalFx.error')}</p>
        ) : null}
      </div>
    );
  };
  const replaced = new Set(
    query.data?.data.flatMap((row) => (row.supersedesId ? [row.supersedesId] : [])) ?? [],
  );
  return (
    <main className="space-y-5 p-4 sm:p-6 [&_button]:min-h-11 [&_input]:min-h-11 [&_select]:min-h-11">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('fiscalFx.title')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('fiscalFx.hint')}</p>
        </div>
        <InlineHelp articleId="finance.vat-rates" label={t('ledger.help')} />
      </header>
      {query.isLoading ? <Skeleton className="h-32" aria-label={t('fiscalFx.loading')} /> : null}
      {query.isError ? (
        <div role="alert">
          <p>{t('fiscalFx.error')}</p>
          <Button onClick={() => void query.refetch()}>{t('fiscalFx.retry')}</Button>
        </div>
      ) : null}
      {query.data?.data.length === 0 ? <p>{t('fiscalFx.empty')}</p> : null}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {query.data?.data.map((rate) => (
          <article key={rate.id} className="space-y-2 rounded-md border p-3">
            <h2 className="font-medium">
              {rate.sourceCurrency} · {t(`fiscalFx.${rate.method}`)}
            </h2>
            <p>
              {rate.quote} · {t(`fiscalFx.${rate.quoteDirection}`)}
            </p>
            <p>
              {rate.validFrom} – {rate.validTo}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('fiscalFx.version')} {rate.version} · {t('fiscalFx.reviewedAt')}{' '}
              {rate.reviewedAt.slice(0, 10)}
            </p>
            <a
              className="break-all text-primary underline"
              href={rate.sourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('fiscalFx.sourceUrl')}
            </a>
            <details>
              <summary>{t('fiscalFx.sourceEvidence')}</summary>
              <p className="whitespace-pre-wrap break-words">{rate.sourceEvidence}</p>
              {rate.approvalEvidence ? (
                <p className="whitespace-pre-wrap break-words">
                  {rate.approvalReference}: {rate.approvalEvidence}
                </p>
              ) : null}
            </details>
            {can('ledger:configure') && !replaced.has(rate.id) ? (
              <Button variant="outline" onClick={() => correct(rate)}>
                {t('fiscalFx.correct')}
              </Button>
            ) : null}
          </article>
        ))}
      </div>
      {can('ledger:configure') ? (
        <form
          ref={formRef}
          onSubmit={(event) => void submit(event)}
          noValidate
          className="grid max-w-3xl gap-4 rounded-md border p-4 sm:grid-cols-2"
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'Enter')) {
              event.preventDefault();
              formRef.current?.requestSubmit();
            }
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="fiscal-method">{t('fiscalFx.method')}</Label>
            <select
              id="fiscal-method"
              className="w-full rounded-md border bg-background px-3"
              {...form.register('method', {
                onChange: () => {
                  for (const name of approvalFields) form.setValue(name, null);
                },
              })}
            >
              <option value="MONTHLY_BMF">{t('fiscalFx.MONTHLY_BMF')}</option>
              <option value="APPROVED_DAILY">{t('fiscalFx.APPROVED_DAILY')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fiscal-direction">{t('fiscalFx.quoteDirection')}</Label>
            <select
              id="fiscal-direction"
              className="w-full rounded-md border bg-background px-3"
              {...form.register('quoteDirection')}
            >
              <option value="FOREIGN_PER_EUR">{t('fiscalFx.FOREIGN_PER_EUR')}</option>
              <option value="EUR_PER_FOREIGN">{t('fiscalFx.EUR_PER_FOREIGN')}</option>
            </select>
          </div>
          {mainFields.map(field)}
          {method === 'APPROVED_DAILY' ? approvalFields.map(field) : null}
          {mutation.isError || Object.keys(form.formState.errors).length ? (
            <div role="alert" className="text-sm text-destructive sm:col-span-2">
              {t('fiscalFx.error')}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button type="submit" disabled={mutation.isPending}>
              {t('fiscalFx.add')}
            </Button>
            {correction ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset(defaults());
                  mutation.reset();
                }}
              >
                {t('fiscalFx.cancel')}
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
      <FiscalSourceReviews rates={query.data?.data ?? []} />
    </main>
  );
}
